import { Grid, Box, Typography, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MissingContent from '../../../../components/shared/errorMessages/MissingContent';
import { ChildTileData } from '../../types/CMSTypes';

interface CMSConsumerApplicationsGridProps {
  childrenData: ChildTileData[];
  spacingValue?: number;
  marginTop?: number;
  marginBottom?: number;
  isHomeware?: boolean;
  onItemClick?: (itemGroupName: string) => void;
  enableScrollMode?: boolean;
}

const CMSConsumerApplicationsGrid: React.FC<CMSConsumerApplicationsGridProps> = ({
                                                                                   childrenData,
                                                                                   spacingValue = 4,
                                                                                   marginTop = 5,
                                                                                   marginBottom = 5,
                                                                                   isHomeware = false,
                                                                                   onItemClick,
                                                                                   enableScrollMode = false,
                                                                                 }) => {
  const navigate = useNavigate();

  const applicationMapping: { [key: string]: string[] } = {
    'Curtaining': ['Curtaining', 'Dual Purpose'],
    'Dual Purpose': ['Dual Purpose'],
    'Upholstery': ['Upholstery', 'Dual Purpose'],
  };

  const formatDisplayText = (child: ChildTileData) => {
    const applicationName = child.application || '';
    const itemGroupName = child.item_group_name || '';
    const categoryName = child.category_name || '';
    const displayName = child.display_name || '';

    if (child.category_type === 'nav_add_on' && displayName) {
      return displayName;
    }

    // Prefer the explicit display_name, then category_name. `application` is now
    // pipe-delimited (e.g. "Curtaining|Dual Purpose"), so it must not be used as
    // the label; it stays last as a legacy fallback.
    let text = displayName || categoryName || applicationName || itemGroupName;

    if (text === 'throws_quilts_and_furs') return 'Throws, Quilts and Furs';
    if (text === 'wall decor') return 'Wall Decor';

    return text.replace(/_/g, ' ');
  };

  // Derive the /custom/:slug route from a nav_add_on's display name or category name
  const getCustomSlug = (child: ChildTileData): string =>
    (child.display_name || child.category_name)
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');

  const handleNavigate = (child: ChildTileData) => {
    const { category_type, item_group, application } = child;

    // nav_add_on: explicit link → dynamic custom page fallback
    if (category_type === 'nav_add_on') {
      if (child.consumer_link_url) {
        window.open(child.consumer_link_url, '_blank');
      } else if (child.consumer_link_category_id) {
        navigate(`/category/${child.consumer_link_category_id}`);
      } else {
        navigate(`/${getCustomSlug(child)}`);
      }
      return;
    }

    switch (category_type) {
      case 'application':
        if (item_group === '005' && application) {
          const applications = applicationMapping[application] || [application];
          const searchParams = new URLSearchParams();
          applications.forEach((app) => searchParams.append('application', app));
          navigate(`/item_group/005/application/${application.toLowerCase()}?${searchParams.toString()}`);
        } else if (item_group && application) {
          navigate(`/item_group/${encodeURIComponent(item_group)}/application/${encodeURIComponent(application)}`);
        }
        break;

      case 'item_group':
        if (item_group) {
          navigate(`/item_group/${encodeURIComponent(item_group)}`);
        }
        break;

      case 'collection':
        if (item_group && child.collection) {
          navigate(`/item_group/${encodeURIComponent(item_group)}/collection/${encodeURIComponent(child.collection)}`);
        }
        break;

      case 'brand':
        if (child.brand) {
          navigate(`/brand/${encodeURIComponent(child.brand)}`);
        }
        break;

      // custom category tiles in children_tile_data — route to custom page
      case 'custom':
        navigate(`/${getCustomSlug(child)}`);
        break;

      default:
        if (item_group === '005' && application) {
          const applications = applicationMapping[application] || [application];
          const searchParams = new URLSearchParams();
          applications.forEach((app) => searchParams.append('application', app));
          navigate(`/item_group/005/application/${application.toLowerCase()}?${searchParams.toString()}`);
        } else if (item_group && application) {
          navigate(`/item_group/${encodeURIComponent(item_group)}/application/${encodeURIComponent(application)}`);
        } else if (item_group) {
          navigate(`/item_group/${encodeURIComponent(item_group)}`);
        } else {
          console.warn('Unable to determine navigation path for:', child);
        }
        break;
    }
  };

  const handleScrollToSection = (child: ChildTileData) => {
    const sectionName = child.category_type === 'nav_add_on'
      ? (child.display_name || child.category_name)
      : (child.category_name || child.item_group_name || child.application);

    if (sectionName && onItemClick) {
      onItemClick(sectionName);
    }
  };

  const handleItemClick = (child: ChildTileData) => {
    if (child.category_type === 'nav_add_on') {
      // Only use configured links if consumer_use_switch is 'on'
      if (child.consumer_use_switch === 'on') {
        if (child.consumer_link_url) {
          window.open(child.consumer_link_url, '_blank');
          return;
        }
        if (child.consumer_link_category_id) {
          navigate(`/category/${child.consumer_link_category_id}`);
          return;
        }
      }
      // No explicit link configured — fall back to the dynamic custom page route
      navigate(`/${getCustomSlug(child)}`);
      return;
    }

    // For non-nav_add_on tiles
    if (enableScrollMode && onItemClick) {
      handleScrollToSection(child);
    } else {
      handleNavigate(child);
    }
  };

  // Hover-zoom toggle. Every tile type now carries its own per-tile switch
  // (consumer_tile_hover_animation_switch) — nav_add_on tiles get it from
  // the navigation tree merge, application/item_group/collection/brand/custom
  // tiles get it directly from children_tile_data. Defaults to enabled when
  // the field is missing (e.g. older cached data) so behaviour doesn't change
  // for tiles that haven't been resaved yet.
  const getHoverAnimationEnabled = (child: ChildTileData): boolean => {
    return child.consumer_tile_hover_animation_switch ?? true;
  };

  if (!childrenData || childrenData.length === 0) {
    return <MissingContent sectionName="Applications Section" />;
  }

  const filteredData = childrenData
    .filter((child) => {
      if (child.category_type === 'nav_add_on') {
        // consumer_tile_display_switch is the correct gate for tile visibility
        // is_active controls nav bar display — not relevant here
        return child.consumer_tile_display_switch === true && !!child.consumer_tile_media_single_url;
      }
      return child.is_published;
    })
    .filter((child) => {
      const displayText = formatDisplayText(child).toLowerCase();
      return displayText !== 'wall decor';
    });

  const processedData = enableScrollMode
    ? filteredData
    : filteredData.sort((a, b) =>
      formatDisplayText(a).toLowerCase().localeCompare(formatDisplayText(b).toLowerCase())
    );

  return (
    <Container maxWidth="xl">
      <Grid
        container
        rowSpacing="16px"
        columnSpacing={{ xs: 1, sm: 2, md: '16px' }}
        justifyContent="center"
        sx={{ mt: marginTop, mb: marginBottom }}
      >
        {processedData.map((child, index) => {
          const imageUrl =
            child.category_type === 'nav_add_on'
              ? child.consumer_tile_media_single_url
              : child.tile_display_media_single_link;

          const hoverAnimationEnabled = getHoverAnimationEnabled(child);

          return (
            <Grid key={child.category_id || index} item xs={12} sm={6} md={4} lg={spacingValue}>
              <Box
                sx={{
                  position: 'relative',
                  cursor: 'pointer',
                  width: '100%',
                  height: { xs: '320px', lg: '510px' },
                  minHeight: '100%',
                  overflow: 'hidden',
                  ...(hoverAnimationEnabled ? { '&:hover img': { transform: 'scale(1.03)' } } : {}),
                }}
                onClick={() => handleItemClick(child)}
              >
                <img
                  src={imageUrl || '/placeholder-image.jpg'}
                  loading="eager"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: hoverAnimationEnabled ? 'transform 0.5s ease' : undefined,
                  }}
                  alt={formatDisplayText(child)}
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                />

                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    background:
                      'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.36) 100%)',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      textAlign: 'center',
                      fontWeight: 'lighter',
                      color: '#fff',
                      fontSize: '40px',
                      // Custom pages are named by hand in the hub: show that name as typed.
                      textTransform: child.category_type === 'custom' ? 'none' : 'capitalize',
                      mb: { xs: 3, lg: 4 },
                    }}
                  >
                    {formatDisplayText(child)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Container>
  );
};

export default CMSConsumerApplicationsGrid;